import { Col, Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import * as St from './StoreSearch.styles';

import {
  useLazyFetchStoresQuery,
  useLazyFetchZipcodeLocationQuery,
} from '~/api/easy-estimator-api/stores-api/storesService';
import { useStores } from '~/api/easy-estimator-api/stores-api/useStores';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { handleIntegerNumberInput } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectZipcode, updateCityAndState } from '~/redux/slices/zipcode';
import PageSubtitle from '~atoms/PageSubtitle';
import PageTitle from '~atoms/PageTitle';
import { useDrawingShapeContext } from '~contexts';
import ZipNotServicedModal from '~organisms/ZipNotServicedModal';
import FullPageLoader from '~templates/FullPageLoader';

type StoreSearchProps = {
  showZipCodeInput?: boolean;
  disabled?: boolean;
  isZipDisabled?: boolean;
  /** Increment to force-reset the field back to the current Redux zipcode */
  zipFieldResetSignal: number;
  onSubmitWithFilledDrawingArea: (newZipcode: string) => void;
  onSubmitWithEmptyDrawingArea: (newZipcode: string) => void;
};

const fieldNameForValidation = 'zipCode';

const zipCodeError = new Error('Invalid zipcode');

const StoreSearch = ({
  showZipCodeInput = true,
  disabled = false,
  isZipDisabled = false,
  zipFieldResetSignal,
  onSubmitWithFilledDrawingArea,
  onSubmitWithEmptyDrawingArea,
}: StoreSearchProps) => {
  const { isOnlineRetailer } = useRetailerSettings();
  const [fetchZipcodeLocation] = useLazyFetchZipcodeLocationQuery();
  const [fetchStores] = useLazyFetchStoresQuery();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const initialZipcode = useAppSelector(selectZipcode);

  const [lastValidZipcode, setLastValidZipcode] = useState(initialZipcode);
  const [isZipNotServicedVisible, setIsZipNotServicedVisible] = useState(false);

  const {
    stores,
    isFetchingStores,
    isSuccess: isStoresSuccess,
    zipCodeLocation,
  } = useStores();

  const { retailerSettings } = useRetailerSettings();
  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();

  const pageTitle = showZipCodeInput
    ? t('STORE_PAGE.TITLE')
    : t('STORE_PAGE.STORES');
  const pageSubTitle = retailerSettings?.isOnlineRetailer
    ? t('STORE_PAGE.SUBTITLE_ONLINE')
    : t('STORE_PAGE.SUBTITLE');

  const inputPlaceholder = t('STORE_PAGE.INPUT_PLACEHOLDER');

  const [_isLoadingZipCodeSearch, setIsLoadingZipCodeSearch] = useState(false);

  const isLoadingZipCodeSearch = isFetchingStores || _isLoadingZipCodeSearch;

  const submitZipCode = useCallback(
    async (value: string) => {
      try {
        flushSync(() => setIsLoadingZipCodeSearch(true));
        await form.validateFields();

        const locationResp = await fetchZipcodeLocation(value);

        // Checking for error, data and that data has any property to consider the zip code valid. No need to specifically check for city
        if (locationResp.isError || !locationResp?.data?.city) {
          throw zipCodeError; // Zipcodes that are not found are invalid
        }

        const storesResp = await fetchStores(
          { params: { zipCode: value } },
          true
        );

        if (storesResp.isError || !storesResp.data) {
          throw zipCodeError;
        }

        if (storesResp.data.length === 0) {
          form.setFields([
            {
              name: fieldNameForValidation,
              value: initialZipcode,
              errors: [],
            },
          ]);
          setLastValidZipcode(initialZipcode);
          setIsZipNotServicedVisible(true);

          return;
        }

        const inputValue = form.getFieldValue(fieldNameForValidation);
        const isDrawingAreaEmpty =
          !inputValue ||
          !areThereShapesInTheDrawingArea ||
          inputValue === initialZipcode;
        const callSubmit = isDrawingAreaEmpty
          ? onSubmitWithEmptyDrawingArea
          : onSubmitWithFilledDrawingArea;

        if (isDrawingAreaEmpty) {
          setLastValidZipcode(inputValue);
        }

        callSubmit(value);
      } catch (error) {
        form.setFields([
          {
            name: fieldNameForValidation,
            value: lastValidZipcode,
            errors: [`Invalid zip code${value ? ':' : ''} ${value}`],
          },
        ]);
      } finally {
        setIsLoadingZipCodeSearch(false);
      }
    },
    [
      areThereShapesInTheDrawingArea,
      fetchStores,
      fetchZipcodeLocation,
      form,
      initialZipcode,
      lastValidZipcode,
      onSubmitWithEmptyDrawingArea,
      onSubmitWithFilledDrawingArea,
    ]
  );

  const onZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    form.setFields([
      {
        name: fieldNameForValidation,
        errors: [],
      },
    ]);

    const value = (event.target as HTMLInputElement).value;

    if (value && value.length === 5 && value !== initialZipcode) {
      submitZipCode(value);
    }
  };

  useEffect(() => {
    form.setFieldsValue({ [fieldNameForValidation]: initialZipcode });
    setLastValidZipcode(initialZipcode);
  }, [form, initialZipcode]);

  useEffect(() => {
    form.setFieldsValue({ [fieldNameForValidation]: initialZipcode });
    form.setFields([{ name: fieldNameForValidation, errors: [] }]);
    setLastValidZipcode(initialZipcode);
  }, [form, initialZipcode, zipFieldResetSignal]);

  useEffect(() => {
    if (
      initialZipcode &&
      isStoresSuccess &&
      !isFetchingStores &&
      stores.length === 0
    ) {
      setIsZipNotServicedVisible(true);
    }
  }, [initialZipcode, isFetchingStores, isStoresSuccess, stores.length]);

  useEffect(() => {
    if (zipCodeLocation?.city || zipCodeLocation?.state) {
      dispatch(
        updateCityAndState({
          city: zipCodeLocation.city,
          state: zipCodeLocation.state,
        })
      );
    }
  }, [dispatch, initialZipcode, zipCodeLocation?.city, zipCodeLocation?.state]);

  if (!retailerSettings) {
    return <FullPageLoader />;
  }

  return (
    <>
      <Col>
        {/** Hide 'stores' title when is online retailer */}
        {isOnlineRetailer || !showZipCodeInput ? null : (
          <PageTitle>{pageTitle}</PageTitle>
        )}
        {showZipCodeInput && (
          <>
            <PageSubtitle>{pageSubTitle}</PageSubtitle>
            <Form
              form={form}
              layout="vertical"
              initialValues={{ zipCode: initialZipcode }}
              onFinish={(form) => submitZipCode(form.zipCode)}
            >
              <St.SubmitContainer>
                <St.SubmitFormItem
                  validateTrigger={false}
                  name={fieldNameForValidation}
                  data-testid="zipcode-search"
                >
                  <St.ZipCodeInput
                    autoFocus={!disabled && !isZipDisabled}
                    type="text"
                    placeholder={inputPlaceholder}
                    maxLength={5}
                    size="large"
                    variant="borderless"
                    allowClear={false}
                    onKeyDown={handleIntegerNumberInput}
                    onChange={onZipCodeChange}
                    loading={isLoadingZipCodeSearch}
                    onSearch={submitZipCode}
                    enterButton
                    disabled={disabled || isZipDisabled}
                  />
                </St.SubmitFormItem>
              </St.SubmitContainer>
            </Form>
          </>
        )}
      </Col>
      <ZipNotServicedModal
        isOnlineRetailer={isOnlineRetailer}
        isVisible={isZipNotServicedVisible}
        onOk={() => setIsZipNotServicedVisible(false)}
      />
    </>
  );
};

export default StoreSearch;
